import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Wrench } from "lucide-react";

export default function InvoicePage() {
  const { orderNumber } = useParams();
  const [o, setO] = useState(null);

  useEffect(() => {
    document.title = `Tax Invoice ${orderNumber} — ACP Online`;
    api.get(`/orders/${orderNumber}`).then((r) => setO(r.data)).catch(() => setO(false));
  }, [orderNumber]);

  if (o === false) return <div className="p-20 text-center">Invoice not found.</div>;
  if (!o) return <div className="p-20 text-center text-muted-foreground">Loading invoice…</div>;

  const store = o.store || {};
  const bank = o.bank_details || {};
  const paid = o.payment?.status === "paid";

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto mb-4 flex justify-between items-center px-4 print:hidden">
        <span className="text-sm text-muted-foreground">Use your browser's print dialog to save as PDF.</span>
        <Button data-testid="print-invoice-btn" onClick={() => window.print()} className="bg-steel text-white hover:bg-steel-light">
          <Printer size={16} className="mr-2" /> Print / Save as PDF
        </Button>
      </div>

      <div data-testid="tax-invoice" className="max-w-3xl mx-auto bg-white shadow-sm print:shadow-none p-10 text-sm text-slate-800">
        {/* header */}
        <div className="flex justify-between items-start border-b-2 border-steel pb-6">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-md bg-accent text-steel"><Wrench size={24} strokeWidth={2.5} /></span>
            <div>
              <div className="font-heading font-extrabold text-2xl tracking-wide text-steel">{store.company_name || "ACP Online"}</div>
              <div className="text-xs text-muted-foreground">{store.address}</div>
              <div className="text-xs text-muted-foreground">{store.contact_email} · {store.contact_phone}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading font-extrabold text-xl">TAX INVOICE</div>
            <div className="font-mono text-accent font-bold">{o.order_number}</div>
            <div className="text-xs text-muted-foreground mt-1">Date: {new Date(o.created_at).toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland" })}</div>
            {store.gst_number && <div className="text-xs text-muted-foreground">GST No: {store.gst_number}</div>}
          </div>
        </div>

        {/* parties */}
        <div className="grid grid-cols-2 gap-6 py-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Bill To</div>
            <div className="font-semibold">{o.customer?.name}</div>
            {o.customer?.company && <div>{o.customer.company}</div>}
            <div>{o.billing_address?.line1}{o.billing_address?.line2 ? `, ${o.billing_address.line2}` : ""}</div>
            <div>{o.billing_address?.city} {o.billing_address?.postcode}</div>
            <div>{o.billing_address?.country}</div>
            <div className="mt-1 text-muted-foreground">{o.customer?.email}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Deliver To</div>
            <div>{o.delivery_address?.line1}{o.delivery_address?.line2 ? `, ${o.delivery_address.line2}` : ""}</div>
            <div>{o.delivery_address?.city} {o.delivery_address?.postcode}</div>
            <div>{o.delivery_address?.country}</div>
            <div className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">Payment</div>
            <div>Bank Transfer · <span className={paid ? "text-emerald-600 font-semibold" : "text-amber-700 font-semibold"}>{paid ? "Paid" : "Awaiting Payment"}</span></div>
            <div className="text-xs text-muted-foreground">Reference: <span className="font-mono">{o.payment?.reference}</span></div>
          </div>
        </div>

        {/* line items */}
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-2">SKU</th><th className="p-2">Description</th>
              <th className="p-2 text-center">Qty</th><th className="p-2 text-right">Unit (incl GST)</th><th className="p-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {o.items.map((i) => (
              <tr key={i.sku} className="border-b">
                <td className="p-2 font-mono text-xs">{i.sku}</td>
                <td className="p-2">{i.name}</td>
                <td className="p-2 text-center">{i.qty}</td>
                <td className="p-2 text-right font-mono">{money(i.unit_price)}</td>
                <td className="p-2 text-right font-mono">{money(i.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* totals */}
        <div className="flex justify-end pt-4">
          <div className="w-64 space-y-1">
            <Row label="Subtotal (incl GST)" value={money(o.subtotal)} />
            <Row label="Shipping" value={o.free_shipping ? "FREE" : money(o.shipping)} />
            <Row label="GST (15%) included" value={money(o.gst_included)} muted />
            <div className="flex justify-between border-t-2 border-steel pt-2 font-bold text-base">
              <span>Total (NZD)</span><span className="font-mono">{money(o.total)}</span>
            </div>
          </div>
        </div>

        {/* bank + footer */}
        <div className="mt-8 rounded-lg border border-accent/40 bg-amber-50/40 p-4 text-xs">
          <div className="font-semibold mb-1">Bank Transfer Instructions</div>
          <div>{bank.bank_name} · {bank.account_name} · <span className="font-mono">{bank.account_number}</span></div>
          <div>Payment Reference: <span className="font-mono font-bold">{o.payment?.reference}</span></div>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          This is a New Zealand tax invoice. All amounts are in NZD and include 15% GST. Thank you for your business.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-muted-foreground" : ""}`}>
      <span>{label}</span><span className="font-mono">{value}</span>
    </div>
  );
}
